import { useState, useEffect, useRef } from "react";
import { Popover, PopoverTrigger, PopoverContent } from "../../popover";
import MagnifyingGlass from "../../../assets/icons/magnifying-glass-small-gray.svg";
import { TimePicker } from "../../time-picker";
import { Button } from "../../button";
import TableFilterState from "./table-filter-state";
import { TimePickerV2 } from "../../time-picker-v2";
import { formatToTimeString } from "../../../lib/date-time";
import dayjs from "dayjs";
import { filter } from "lodash";

type PopoverTimeFilterV2Props<T = any> = {
  filterState: TableFilterState<T>;
  placeholder?: string | null;
  title?: string | null;
  description?: string | null;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
};

const PopoverTimeFilterV2Component = <T extends Record<string, any>>({
  filterState,
  placeholder,
  title,
  description,
  isOpen = false,
  onOpenChange,
}: PopoverTimeFilterV2Props<T>) => {
  const [currentValue, setCurrentValue] = useState<string>("");

  // Check if filter has value
  const hasValue =
    filterState.filter[filterState.filterKey] &&
    filterState.filter[filterState.filterKey].trim() !== "";

  // Use controlled state if provided, otherwise use internal logic
  const controlledOpen = isOpen !== undefined ? isOpen : hasValue || false;
  const controlledOnOpenChange = onOpenChange || (() => {});

  const handleOpenChange = (newOpen: boolean) => {
    controlledOnOpenChange(newOpen);
  };

  const handleTimeChange = (time: string) => {
    filterState.setFilter((prev) => ({
      ...prev,
      [filterState.filterKey]: time,
    }));
  };

  const getCurrentValue = () => {
    if (currentValue === "") {
      return "";
    }
    if (dayjs(currentValue, "HH:mm:ss", true).isValid()) {
      return currentValue;
    }
    return formatToTimeString(new Date(currentValue));
  };

  useEffect(() => {
    setCurrentValue(filterState.filter[filterState.filterKey]);
  }, [filterState]);

  return (
    <Popover
      open={controlledOpen}
      onOpenChange={handleOpenChange}
    >
      <PopoverTrigger asChild>
        <img
          className="ml-2 w-3 cursor-pointer"
          src={MagnifyingGlass}
          alt="Search"
        />
      </PopoverTrigger>
      <PopoverContent className="w-auto p-3">
        <div className="space-y-3">
          {title && (
            <span className="block my-[10px] mx-[5px] text-xs text-[#6A7282]">{title}</span>
          )}
          <TimePickerV2
            value={getCurrentValue()}
            onChange={(value) => {
              handleTimeChange(value);
            }}
          />
          <div className="flex justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                filterState.setFilter((prev) => ({
                  ...prev,
                  [filterState.filterKey]: "",
                }));
                setCurrentValue("");
              }}
              className="text-xs"
            >
              Clear Filter
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default PopoverTimeFilterV2Component;
